import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Shield,
  ShieldCheck,
  ShieldX,
  Users
} from 'lucide-react'
import { roleService } from '../services/roleService'
import { LoadingSpinner, Button } from '../components'
import toast from 'react-hot-toast'

function Roles() {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingRole, setEditingRole] = useState(null)

  const queryClient = useQueryClient()

  // Fetch roles
  const { data: rolesData, isLoading } = useQuery(
    ['roles', { page: currentPage, search: searchTerm }],
    () => roleService.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm,
    })
  )

  // Create role mutation
  const createRoleMutation = useMutation(roleService.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('roles')
      setShowModal(false)
      toast.success('Role created successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create role')
    },
  })

  // Update role mutation
  const updateRoleMutation = useMutation(
    ({ id, data }) => roleService.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles')
        setShowModal(false)
        setEditingRole(null)
        toast.success('Role updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update role')
      },
    }
  )

  // Delete role mutation
  const deleteRoleMutation = useMutation(roleService.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('roles')
      toast.success('Role deleted successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete role')
    },
  })

  // Toggle status mutation
  const toggleStatusMutation = useMutation(roleService.toggleStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries('roles')
      toast.success('Role status updated successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update role status')
    },
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  const onSubmit = (data) => {
    if (editingRole) {
      updateRoleMutation.mutate({ id: editingRole.id, data })
    } else {
      createRoleMutation.mutate(data)
    }
  }

  const handleEdit = (role) => {
    setEditingRole(role)
    reset({
      name: role.name,
      description: role.description,
      permissions: JSON.stringify(role.permissions, null, 2),
    })
    setShowModal(true)
  }

  const handleDelete = (role) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      deleteRoleMutation.mutate(role.id)
    }
  }

  const handleToggleStatus = (role) => {
    toggleStatusMutation.mutate(role.id)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingRole(null)
    reset()
  }

  const roles = rolesData?.data?.roles || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Roles</h1>
          <p className="mt-1 text-base text-secondary-600">
            Manage user roles and permissions
          </p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          variant="primary"
          size="md"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Role
        </Button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <button
              onClick={() => setSearchTerm('')}
              className="btn-outline"
            >
              <Filter className="h-4 w-4 mr-2" />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Roles Table */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <LoadingSpinner size="lg" className="h-64" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr className="table-row">
                    <th className="table-head">Name</th>
                    <th className="table-head">Description</th>
                    <th className="table-head">Users</th>
                    <th className="table-head">Status</th>
                    <th className="table-head">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {roles.map((role) => (
                    <tr key={role.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <Shield className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-secondary-900">
                              {role.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {role.description || 'No description'}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-secondary-900">
                          <Users className="h-4 w-4 mr-1" />
                          {role.users?.length || 0} users
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${role.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {role.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(role)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(role)}
                            className={role.isActive ? 'text-danger-600 hover:text-danger-900' : 'text-success-600 hover:text-success-900'}
                          >
                            {role.isActive ? <ShieldX className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => handleDelete(role)}
                            className="text-danger-600 hover:text-danger-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Role Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="modal-overlay" onClick={handleCloseModal} />
            <div className="modal-content max-w-2xl">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                {editingRole ? 'Edit Role' : 'Add New Role'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="label">Role Name</label>
                  <input
                    {...register('name', { required: 'Role name is required' })}
                    className="input"
                    placeholder="Enter role name"
                  />
                  {errors.name && (
                    <p className="form-error">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea
                    {...register('description')}
                    className="input"
                    rows={3}
                    placeholder="Enter role description"
                  />
                </div>
                <div>
                  <label className="label">Permissions (JSON)</label>
                  <textarea
                    {...register('permissions')}
                    className="input font-mono text-sm"
                    rows={8}
                    placeholder='{"customers": ["create", "read", "update", "delete"], "orders": ["create", "read"]}'
                  />
                  <p className="form-help">
                    Enter permissions as JSON object. Available modules: customers, measurements, stocks, orders, payments, expenses, accounts, bank-details
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    onClick={handleCloseModal}
                    variant="outline"
                    size="md"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createRoleMutation.isLoading || updateRoleMutation.isLoading}
                    variant="primary"
                    size="md"
                    loading={createRoleMutation.isLoading || updateRoleMutation.isLoading}
                  >
                    {editingRole ? 'Update Role' : 'Create Role'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Roles
